// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2025 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconProps, IconWrapper } from "@insite/mobius/Icon";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import TypographyStyle from "@insite/mobius/Typography/TypographyStyle";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { XNGAGE_CUSTOM_WIDGET } from "@synd/XDL/constants";
import Close from "@synd/XDL/Icons/Close";
import { fontConfig } from "@synd/XDL/PreStyle/typography";
import { color, ellipsis, getSystemColor, layoutContainer, xParserOptions } from "@synd/XDL/Utilities";
import parse from "html-react-parser";
import React, { FC, useState } from "react";
import { css } from "styled-components";

const enum fields {
    backgroundColor = "backgroundColor",
    message = "message",
    dismissible = "dismissible",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.backgroundColor]: string;
        [fields.message]: string;
        [fields.dismissible]: boolean;
    };
}
type Props = OwnProps;
export interface GlobalWebsiteMessageStyles {
    container?: InjectableCss;
    wrapper?: InjectableCss;
    messageWrapper?: InjectableCss;
    closeClickable?: ClickablePresentationProps;
    closeIcon?: IconProps;
    message?: TypographyPresentationProps;
}
export const globalWebsiteMessageStyles: GlobalWebsiteMessageStyles = {
    wrapper: {
        css: css`
            ${({ backgroundColor }: any) =>
                backgroundColor &&
                css`
                    background: ${backgroundColor};
                `}
        `,
    },
    messageWrapper: {
        css: css`
            position: relative;
            display: flex;
            align-items: center;
            justify-content: center;
            height: 34px;
            ${layoutContainer}
        `,
    },
    closeClickable: {
        css: css`
            &:focus {
                outline: none;
            }
            &:hover > ${IconWrapper} {
                color: ${color("common.ui11")};
            }
        `,
    },
    closeIcon: {
        color: "common.white",
        src: Close,
        width: 9,
        height: 13,
    },
    message: {
        mergeCss: true,
        color: "common.white",
        size: fontConfig.xSmallSingleLine.size,
        lineHeight: fontConfig.xSmallSingleLine.lineHeight,
        weight: fontConfig.weight.bold,
        css: css`
            letter-spacing: 0;
            display: flex;
            justify-content: center;
            width: 100%;
            margin-bottom: 0;
            ${TypographyStyle} {
                font-size: ${fontConfig.xSmallSingleLine.size}px;
                color: ${color("common.white")};
                font-weight: ${fontConfig.weight.semiBold};
                letter-spacing: 0;
                line-height: ${fontConfig.xSmallSingleLine.lineHeight};
                max-width: 90%;
                margin: 0;
                ${ellipsis(1)}
                &:empty {
                    display: none;
                }
            }
        `,
    },
};
const styles = globalWebsiteMessageStyles;
const GlobalWebsiteMessage: FC<Props> = ({ fields: { backgroundColor, message, dismissible } }) => {
    const [showGlobalMessage, setShowGlobalMessage] = useState(true);

    const onClose = () => {
        setShowGlobalMessage(false);
    };

    if (!showGlobalMessage || !message) {
        return null;
    }
    return (
        <StyledWrapper {...styles.wrapper} {...{ backgroundColor }} data-test-selector="global-message-widget">
            <StyledWrapper {...styles.messageWrapper}>
                <Typography {...styles.message} data-test-selector="global_site_message">
                    {parse(message, xParserOptions)}
                </Typography>
                {dismissible && (
                    <Clickable
                        {...styles.closeClickable}
                        onClick={onClose}
                        data-test-selector="global_site_message_close"
                        aria-label="Global Site Message Close Clickable"
                    >
                        <Icon {...styles.closeIcon} />
                    </Clickable>
                )}
            </StyledWrapper>
        </StyledWrapper>
    );
};
const widgetModule: WidgetModule = {
    component: GlobalWebsiteMessage,
    definition: {
        group: XNGAGE_CUSTOM_WIDGET,
        displayName: "Global Website Message",
        allowedContexts: ["Header"],
        fieldDefinitions: [
            {
                name: fields.backgroundColor,
                displayName: "Background Color",
                editorTemplate: "ColorPickerField",
                defaultValue: getSystemColor("info.main"),
                fieldType: "General",
                isRequired: true,
            },
            {
                name: fields.message,
                displayName: "Global Message Content",
                editorTemplate: "RichTextField",
                defaultValue: "",
                fieldType: "General",
                placeholder: "Add your message here",
                isRequired: false,
            },
            {
                name: fields.dismissible,
                displayName: "Dismissible?",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
                isRequired: false,
            },
        ],
    },
};
export default widgetModule;
